import type { CaptureConstraints } from "./media-devices.js";

/**
 * The recording model shared by main, preload and both renderers (ticket 12,
 * FR-43 to FR-49).
 *
 * The engine is split across two processes by necessity: `MediaRecorder` and
 * `getUserMedia` exist only in a renderer, while the session — its state, its
 * bytes on disk, its output file — belongs in the main process with the rest of
 * application state (PRD §8). This module is the part that belongs to neither:
 * every rule that can be decided without a camera or a filesystem, so it can be
 * unit-tested in Node and stated in exactly one place.
 *
 * It is also the validation boundary for capture events, which arrive from a
 * renderer over IPC — {@link asCaptureEvent} takes `unknown` and discards what
 * does not fit rather than trusting the shape.
 */

/**
 * Where a take is in its life. `counting`, `starting` and `stopping` are
 * separate from `recording` because each takes real time — counting the user
 * back in, opening the camera, then remuxing — and FR-49 says the user must
 * never be unsure whether they are rolling, which means those moments have to
 * be nameable rather than rounded to the nearest of their neighbours.
 */
export type RecordingPhase =
  | "idle"
  | "counting"
  | "starting"
  | "recording"
  | "paused"
  | "stopping";

/** The recording state both windows are views onto, broadcast on every change. */
export interface RecordingStatus {
  phase: RecordingPhase;
  /** Milliseconds of *captured* media so far — paused time is excluded, so this
   * matches the duration of the file that will be written (FR-43). */
  elapsedMs: number;
  /** The number the countdown is currently showing (FR-44), counting down to
   * one. Present only while `phase` is `counting`. */
  countdown?: number;
  /** The MP4 the last completed take was written to (FR-48). */
  lastOutput?: string;
  /** Why the last take failed, if it did. Cleared when the next one starts. */
  error?: string;
}

/** The status before anything has been recorded. */
export const IDLE_STATUS: RecordingStatus = { phase: "idle", elapsedMs: 0 };

/** How each phase is described in the control window, where there is room for a
 * sentence and the user is at the keyboard. */
export const RECORDING_PHASE_LABEL: Record<RecordingPhase, string> = {
  idle: "Ready",
  counting: "Counting down…",
  starting: "Opening camera…",
  recording: "Recording",
  paused: "Paused",
  stopping: "Saving…",
};

/** How each phase is stamped on the overlay badge: one short word, because it
 * is read from filming distance rather than at the desk (FR-49). `counting` is
 * blank because the countdown draws its own number across the whole overlay —
 * a second, smaller word beside it would only compete with it. */
export const RECORDING_PHASE_BADGE: Record<RecordingPhase, string> = {
  idle: "",
  counting: "",
  starting: "STARTING",
  recording: "REC",
  paused: "PAUSED",
  stopping: "SAVING",
};

/**
 * What the countdown counts from (FR-44). Three is the spec's number and it is
 * about the right length: long enough to put the keyboard down and get back in
 * frame, short enough not to be waited through on every retake.
 */
export const COUNTDOWN_FROM = 3;

/** How long each number is held for. One second, so "3-2-1" takes the three
 * seconds the user is counting on rather than an arbitrary stretch. */
export const COUNTDOWN_TICK_MS = 1_000;

/**
 * How often `MediaRecorder` is asked to hand over what it has buffered, which
 * is also the ceiling on how much footage a crash can cost (FR-45): the bytes
 * are on disk within a chunk of being captured, so an interruption loses at
 * most the last second rather than the take.
 */
export const CHUNK_INTERVAL_MS = 1_000;

/**
 * Whether a take is in progress in any sense. Used to lock the things that have
 * no safe outcome mid-take: refresh (FR-17) and the device picker.
 * Deliberately true during `stopping` as well — the remux is still writing the
 * file the user is waiting for.
 */
export function isRecordingActive(phase: RecordingPhase): boolean {
  return phase !== "idle";
}

/**
 * Whether the take still needs the device it opened (mobile-app ticket 24).
 *
 * The question a host asks when the phone is taken away from the reader — a
 * call, the home gesture — and has to decide whether to hand the microphone
 * back. A take that may yet be resumed keeps it, because closing the one device
 * the resume has to come back to makes resuming a second open, and a second
 * open is a second file (FR-31). `counting` and `starting` are on the way to
 * such a take, with the device already opened for it.
 *
 * Narrower than {@link isRecordingActive} by exactly `stopping`, and that is the
 * point: a take being written has no resume to come back to, so it has no claim
 * on the device — and on a phone `stopping` is not a moment but a stream copy of
 * every segment, long enough for a microphone to go on reading behind an app
 * nobody is looking at.
 *
 * The same phases {@link stopIsWorthConfirming} allows, for a different reason,
 * and stated separately because the two can come apart.
 */
export function takeHoldsTheDevice(phase: RecordingPhase): boolean {
  return phase !== "idle" && phase !== "stopping";
}

/**
 * Whether the pause control means anything right now (FR-43).
 *
 * There is nothing to pause during the countdown or while the file is being
 * written, and `MediaRecorder.pause` on a recorder that has not been started
 * throws — so this is the same question {@link RecordingController.togglePause}
 * asks before acting, and the transports draw themselves from it rather than
 * each keeping a copy. Two copies of a rule about which controls are live is how
 * one host ends up offering a button the other host's engine refuses.
 */
export function canTogglePause(phase: RecordingPhase): boolean {
  return phase === "recording" || phase === "paused";
}

/**
 * Whether stopping now would be a *cancel* rather than a save.
 *
 * During the countdown `go` has not gone out, so the recorder never rolled and
 * not a byte was captured: there is no file for a stop to produce. A button that
 * said "Stop" there would be promising one — which is the same reasoning
 * `RecordingController` gives for routing that case away from `stopping`
 * entirely, stated once for the buttons that have to say so.
 */
export function stopIsCancel(phase: RecordingPhase): boolean {
  return phase === "counting";
}

/**
 * Whether stamping a take marker means anything right now (story 67).
 *
 * The same two phases {@link canTogglePause} allows, reached from the other end
 * and worth stating separately because they can come apart. A marker is a point
 * in the *video file*, so it needs the two things only these phases have: some
 * footage for it to point into, and a captured-milliseconds count to name the
 * point with. During the countdown and the start there is no file yet, and by
 * `stopping` the sidecar is already being assembled from what the take had —
 * a marker landing there would be one the file may or may not carry depending on
 * which callback ran first, which is worse than one that plainly did nothing.
 *
 * Pausing is deliberately included. The reader who pauses to fix a light and
 * wants the last minute marked bad is asking a reasonable question, and the
 * answer — the moment the video resumes — is the same one the sidecar gives
 * every word read during a pause.
 */
export function canMarkTake(phase: RecordingPhase): boolean {
  return phase === "recording" || phase === "paused";
}

/**
 * Whether a *spoken* stop has anything to confirm right now (FR-56).
 *
 * Narrower than {@link isRecordingActive}: once the file is being written there is
 * nothing left for a confirmed stop to do, so a confirmation window opened there
 * would put a question on the prompter that answering does nothing about — which
 * is worse than not asking. Cancelling a countdown by voice is fair game, and a
 * spoken `stop` already means cancel during one.
 *
 * Read by both hosts rather than kept twice, for the reason {@link canTogglePause}
 * is: two copies of a rule about when a control means something is how one host
 * ends up asking a question the other host's engine would refuse to act on.
 */
export function stopIsWorthConfirming(phase: RecordingPhase): boolean {
  return phase !== "idle" && phase !== "stopping";
}

/**
 * Capture containers in the order a host **with a remuxer** prefers them — the
 * desktop app — most preferred first.
 *
 * H.264 first, and not for quality: FR-46 calls the final step a *remux*, and
 * that is only true if the video is already H.264 — ffmpeg copies the stream
 * instead of re-encoding it, so stopping a twenty-minute take costs seconds
 * rather than minutes and the pixels are the ones the camera produced. The VP9
 * and VP8 entries are the fallback for a Chromium build without an H.264
 * encoder, and cost a real transcode on stop.
 *
 * Matroska before WebM for the same bytes because it is the container Chromium
 * will actually put H.264 in.
 *
 * **Matroska ahead of the direct-mux MP4 entries is the measured choice, not an
 * oversight** (web ticket 01, `CONTAINER-PROBE.md`). Every Chromium measured
 * will mux H.264 and AAC into MP4 directly — but it can only close an MP4
 * fragment on a keyframe, so `ondataavailable` fires roughly every five seconds
 * however short a timeslice it is given, and the first fragment lands ten to
 * fifteen seconds in. Matroska honours the one-second timeslice exactly. Since
 * this host is going to run a stream copy either way, it buys FR-45's
 * one-second bound on what a crash costs for a price it was paying anyway. The
 * MP4 entries sit below both H.264 containers and above the VP-family ones,
 * because a capture that only needs copying beats one that needs transcoding.
 */
export const CAPTURE_MIME_PREFERENCES: readonly string[] = [
  "video/x-matroska;codecs=avc1,opus",
  "video/webm;codecs=h264,opus",
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

/**
 * The same containers in the order a host **without a remuxer** prefers them —
 * the browser, where in-browser transcoding is rejected outright and whatever
 * is captured is what the user is handed.
 *
 * Direct-mux MP4 first, therefore: it is the only container on this list a
 * video editor takes without complaint, and recording it means there is no
 * conversion step to be unable to perform. The cost is the coarser chunk
 * cadence described above, which is the right trade only when the alternative
 * is handing someone a `.mkv` and an apology.
 *
 * Both spellings of H.264-with-AAC are listed because a build that accepts only
 * the profile-qualified form would otherwise fall all the way through to a
 * container it cannot deliver. Below them the order is the capture list's, as
 * the honest fallback: capture what this browser can, and say so.
 */
export const DELIVERABLE_MIME_PREFERENCES: readonly string[] = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/x-matroska;codecs=avc1,opus",
  "video/webm;codecs=h264,opus",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

/**
 * The best capture container this Chromium supports, or null when it supports
 * none of them — in which case the caller constructs `MediaRecorder` without a
 * `mimeType` and reads back whatever it chose. Only a renderer can answer
 * `isTypeSupported`, so the choice is made there and reported to main.
 *
 * The order is a parameter rather than a constant because the right container
 * depends on the host, not on the browser: see the two lists above.
 */
export function chooseCaptureMimeType(
  isSupported: (mimeType: string) => boolean,
  preferences: readonly string[] = CAPTURE_MIME_PREFERENCES,
): string | null {
  return preferences.find((type) => isSupported(type)) ?? null;
}

/**
 * The file extension for a capture mime type — the chunk file has to be named
 * for what it actually contains, or ffmpeg guesses the demuxer from the wrong
 * extension when the session is recovered later (ticket 13).
 */
export function containerExtension(mimeType: string): string {
  const base = mimeType.split(";")[0]?.trim().toLowerCase() ?? "";
  if (base === "video/x-matroska") return "mkv";
  if (base === "video/mp4") return "mp4";
  return "webm";
}

/** The codecs a mime type names, in the order it names them. Video first, audio
 * second — that is the order in every string Chromium produces and the order it
 * accepts. */
function codecsOf(mimeType: string): string[] {
  const match = /codecs\s*=\s*"?([^";]+)"?/i.exec(mimeType);
  return (match?.[1] ?? "").split(",").map((codec) => codec.trim().toLowerCase());
}

/** The video codec named in a mime type's `codecs` parameter, or undefined when
 * it names none. */
export function videoCodecOf(mimeType: string): string | undefined {
  return codecsOf(mimeType)[0] || undefined;
}

/** Whether a codec string is H.264 in any of its spellings — the profile suffix
 * (`avc1.42E01E`) varies by build, and Chromium uses both `avc1` and `h264`. */
export function isAvcCodec(codec: string | undefined): boolean {
  if (!codec) return false;
  return /^(avc1|avc3|h264)/.test(codec);
}

/** The audio codec named in a mime type's `codecs` parameter, or undefined when
 * it names none. */
export function audioCodecOf(mimeType: string): string | undefined {
  return codecsOf(mimeType)[1] || undefined;
}

/** Whether a codec string is AAC. `mp4a.40.2` is how MP4 spells it — the object
 * type indicator varies with the profile, so only the prefix is matched. */
export function isAacCodec(codec: string | undefined): boolean {
  if (!codec) return false;
  return /^(mp4a|aac)/.test(codec);
}

function pad(value: number, width = 2): string {
  return String(value).padStart(width, "0");
}

/**
 * The id for a take, from its local start time. Sortable, filename-safe, and
 * the name of the directory its chunks live in until they are remuxed.
 *
 * Milliseconds are included because the id has to be *unique*, not merely
 * descriptive: a take whose remux failed keeps its chunks on disk for recovery,
 * and a second take starting in the same second would otherwise reuse the
 * directory and overwrite them. The output filename (see
 * {@link outputFileName}) has no such constraint and stays readable.
 */
export function sessionId(startedAt: Date): string {
  return (
    `${startedAt.getFullYear()}${pad(startedAt.getMonth() + 1)}${pad(startedAt.getDate())}` +
    `-${pad(startedAt.getHours())}${pad(startedAt.getMinutes())}${pad(startedAt.getSeconds())}` +
    `-${pad(startedAt.getMilliseconds(), 3)}`
  );
}

/** How much of a document title survives into the filename before it is cut. */
const MAX_TITLE_LENGTH = 80;

/** The characters Windows will not accept in a filename, plus the control
 * range. Replaced with a space rather than deleted so `Q1:growth` does not
 * become `Q1growth`. */
const ILLEGAL_FILENAME_CHARS = /[<>:"/\\|?*\u0000-\u001f]/g;

/**
 * A document title reduced to something Windows will store (FR-48). Every
 * output name is prefixed with a date, so the result can never collide with a
 * reserved device name (`CON`, `NUL`) on its own and none is checked for.
 */
export function sanitiseFileTitle(title: string | undefined): string {
  const cleaned = (title ?? "")
    .replace(ILLEGAL_FILENAME_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim();
  const truncated =
    cleaned.length > MAX_TITLE_LENGTH
      ? // Prefer to lose a whole word rather than cut one in half, but take the
        // hard cut if the title has no spaces to break on.
        (cleaned.slice(0, MAX_TITLE_LENGTH).replace(/\s+\S*$/, "") ||
          cleaned.slice(0, MAX_TITLE_LENGTH))
      : cleaned;
  // Windows silently drops trailing dots and spaces, which would make the name
  // on disk differ from the one cuekards reports having written.
  const trimmed = truncated.replace(/[. ]+$/, "");
  return trimmed === "" ? "Untitled" : trimmed;
}

/**
 * The output filename for a take (FR-48): date first so the folder sorts
 * chronologically, then the time to separate takes made the same day, then the
 * document title so a take is recognisable without opening it.
 *
 * The extension defaults to `mp4` because that is what a host with a remuxer
 * always produces. A host without one hands over whatever it captured, and has
 * to say so in the name: an `.mp4` on a Matroska file is a file the user's
 * editor refuses for a reason nothing on screen explains (web ticket 11).
 */
export function outputFileName(
  startedAt: Date,
  title: string | undefined,
  extension = "mp4",
): string {
  const date = `${startedAt.getFullYear()}-${pad(startedAt.getMonth() + 1)}-${pad(startedAt.getDate())}`;
  const time = `${pad(startedAt.getHours())}${pad(startedAt.getMinutes())}${pad(startedAt.getSeconds())}`;
  return `${date} ${time} ${sanitiseFileTitle(title)}.${extension}`;
}

/**
 * A filename that is not already taken, by appending ` (2)`, ` (3)` and so on
 * before the extension.
 *
 * The output name is derived from a timestamp and a title, which is readable
 * but not unique — two takes of the same script in the same second, or an
 * interrupted session recovered later, can land on the same name. ffmpeg is
 * invoked with `-y`, so without this the second one would silently overwrite
 * the first, and overwriting footage is the one outcome this whole engine
 * exists to prevent.
 *
 * The bound is a guard against an `exists` that always answers true; reaching
 * it would need a thousand identically-named takes, at which point reusing the
 * name is the least of it.
 */
export function uniqueFileName(
  name: string,
  exists: (candidate: string) => boolean,
): string {
  if (!exists(name)) return name;
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const extension = dot > 0 ? name.slice(dot) : "";
  for (let n = 2; n < 1_000; n++) {
    const candidate = `${stem} (${n})${extension}`;
    if (!exists(candidate)) return candidate;
  }
  return name;
}

/**
 * A document's name taken from its own first line. The Docs plain-text export
 * carries the real title in a header, but a script pasted from anywhere else
 * does not, and its first line is what the user would call it.
 */
export function scriptTitle(text: string): string | undefined {
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed !== "") return trimmed;
  }
  return undefined;
}

/** The two fields elapsed time is derived from: what earlier running stretches
 * added up to, and when the current one began (null while paused). */
export interface ElapsedState {
  accumulatedMs: number;
  runningSince: number | null;
}

/**
 * Captured milliseconds at a given instant. Paused time never enters the sum,
 * which is what makes the count match the duration of the single continuous
 * file pause/resume produces (FR-43). Clamped at the accumulated total so a
 * clock that steps backwards cannot make the take appear to shrink.
 */
export function elapsedMs(state: ElapsedState, now: number): number {
  if (state.runningSince === null) return state.accumulatedMs;
  return state.accumulatedMs + Math.max(0, now - state.runningSince);
}

/** Elapsed time as a timecode: `mm:ss`, growing an hours field only when there
 * is one, so the common case stays narrow enough for the overlay chip. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1_000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3_600);
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * What to do about a named `getUserMedia` failure, or null when it is not one of
 * them. These four `DOMException`s are the ways opening a device fails in
 * practice and each has a different fix, so none is worth flattening into
 * "couldn't open the hardware".
 *
 * Shared by the recorder and by the follower's rehearsal microphone (ticket 14):
 * the same Windows setting, the same app holding the device, the same missing
 * hardware, whichever half of cuekards asked for it. Only the fallback wording
 * differs, which is what the two callers below supply.
 */
function deviceFailureAdvice(name: string): string | null {
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return (
        "Windows is blocking access to the camera or microphone. Allow desktop " +
        "apps to use them in Settings → Privacy & security → Camera and Microphone."
      );
    case "NotReadableError":
    case "AbortError":
      return (
        "The camera or microphone is in use by another app. Close whatever is " +
        "holding it and try again."
      );
    case "NotFoundError":
      return "No camera or microphone is connected.";
    case "OverconstrainedError":
      return (
        "The camera can't record at the requested resolution or framerate. " +
        "Set them back to native in Devices and try again."
      );
    default:
      return null;
  }
}

function errorName(error: unknown): string {
  return typeof error === "object" && error !== null && "name" in error
    ? String((error as { name: unknown }).name)
    : "";
}

function errorMessage(error: unknown): string {
  return typeof error === "object" && error !== null && "message" in error
    ? String((error as { message: unknown }).message)
    : String(error);
}

/**
 * Why a take is refused when every device has been switched off (web-ui ticket
 * 06).
 *
 * Neither switch on its own takes a take away — that is the whole reason the
 * green room says what each one *costs* rather than refusing it. Both together
 * are the one combination that leaves nothing to record, and the alternative to
 * this sentence is `getUserMedia({})`, which throws a `TypeError` whose message
 * is about the Web IDL of `getUserMedia` rather than about a button somebody
 * pressed.
 *
 * One string for two places, because a tooltip on the dead button and the
 * refusal a hotkey gets are the same fact said at two moments.
 */
export const NOTHING_TO_RECORD = "Turn the camera or microphone on first";

/** Why capture could not be opened, phrased as the thing to go and do about it. */
export function captureFailureMessage(error: unknown): string {
  return (
    deviceFailureAdvice(errorName(error)) ??
    `Couldn't start recording: ${errorMessage(error)}`
  );
}

/**
 * Why the microphone could not be opened for the follower alone (FR-37's
 * rehearsal). Worded for rehearsal rather than for a take, because the two fail
 * at different moments and a message about recording would be confusing when
 * nothing was being recorded.
 */
export function micFailureMessage(error: unknown): string {
  return (
    deviceFailureAdvice(errorName(error)) ??
    `Couldn't open the microphone: ${errorMessage(error)}`
  );
}

/**
 * What main asks the capturing renderer to do. Commands are one-way; the
 * renderer answers with {@link CaptureEvent}s.
 *
 * Opening capture and beginning it are two commands rather than one, and that
 * split is what FR-44's countdown is built on. Opening a webcam takes real
 * time — a cold one can take seconds — so it happens *during* the countdown,
 * concurrently with the numbers the user is watching. `go` then costs nothing
 * but `MediaRecorder.start`, so the first frame lands as the countdown ends
 * rather than however long the hardware takes after it.
 */
export type CaptureCommand =
  | {
      kind: "open";
      sessionId: string;
      constraints: CaptureConstraints;
      timesliceMs: number;
    }
  | { kind: "go"; sessionId: string }
  | { kind: "pause" }
  | { kind: "resume" }
  | { kind: "stop" }
  /**
   * Open the microphone for the follower alone, with no take and no camera — the
   * rehearsal case (FR-37, ticket 14).
   *
   * It is a *capture* command rather than its own channel because of FR-31: the
   * microphone must be opened exactly once, so exactly one thing in the renderer
   * may call `getUserMedia`. Routing rehearsal through the same engine is what
   * makes that structural instead of a convention two modules have to keep.
   */
  | { kind: "listen"; audio: MediaTrackConstraints }
  /** Stop listening for rehearsal. Does not touch a take in progress — the
   * follower always runs while recording (FR-37). */
  | { kind: "unlisten" };

/** What the capturing renderer reports back. The take events carry a session id,
 * so an event from a take that has already ended is discarded rather than
 * appended to the next one's file. */
export type CaptureEvent =
  /** The devices are open and a recorder is built, but not rolling — it is
   * waiting for `go`. The container is known from this moment, which is what
   * lets main have the chunk file open before the countdown ends. */
  | { kind: "opened"; sessionId: string; mimeType: string }
  /** The recorder is rolling; from here on, chunks arrive. */
  | { kind: "started"; sessionId: string }
  | { kind: "chunk"; sessionId: string; data: Uint8Array }
  | { kind: "stopped"; sessionId: string }
  | { kind: "failed"; sessionId: string; message: string }
  /**
   * Whether microphone audio is reaching the follower (ticket 14). Session-free,
   * because the fork outlives any one take: it is attached to a take's own stream
   * while one is rolling and to a rehearsal stream otherwise, and main needs to
   * know which without inferring it from the recording phase.
   */
  | { kind: "listening"; listening: boolean }
  /** The rehearsal microphone could not be opened. Never fails a take — there
   * isn't one. */
  | { kind: "listenFailed"; message: string };

function stringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * Narrow an unvalidated capture event from a renderer, or null when it is not
 * one. Chunks are copied into a `Uint8Array` view because Electron's structured
 * clone can deliver the bytes as either a typed array or a raw `ArrayBuffer`
 * depending on how the renderer handed them over.
 */
export function asCaptureEvent(value: unknown): CaptureEvent | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;

  // The follower's two events belong to no take, so they are narrowed before the
  // session id is demanded.
  if (record["kind"] === "listening") {
    return typeof record["listening"] === "boolean"
      ? { kind: "listening", listening: record["listening"] }
      : null;
  }
  if (record["kind"] === "listenFailed") {
    const message = stringField(record, "message");
    return message === null ? null : { kind: "listenFailed", message };
  }

  const id = stringField(record, "sessionId");
  if (id === null) return null;
  switch (record["kind"]) {
    case "opened": {
      const mimeType = stringField(record, "mimeType");
      return mimeType === null
        ? null
        : { kind: "opened", sessionId: id, mimeType };
    }
    case "started":
      return { kind: "started", sessionId: id };
    case "chunk": {
      const data = record["data"];
      if (ArrayBuffer.isView(data)) {
        return {
          kind: "chunk",
          sessionId: id,
          data: new Uint8Array(data.buffer, data.byteOffset, data.byteLength),
        };
      }
      if (data instanceof ArrayBuffer) {
        return { kind: "chunk", sessionId: id, data: new Uint8Array(data) };
      }
      return null;
    }
    case "stopped":
      return { kind: "stopped", sessionId: id };
    case "failed": {
      const message = stringField(record, "message");
      return message === null
        ? null
        : { kind: "failed", sessionId: id, message };
    }
    default:
      return null;
  }
}
